import _ from 'lodash';
import * as d3 from 'd3';
import moment from 'moment';
import { LevelColoursMixin } from '../../../utility/level-colours-mixin';

// we'll anchor to the first reading as long as it doesn't differ from the requested window start by more than this
const ANCHOR_TOLERANCE_HOURS = 6;
const READING_INTERVAL = 4;
const MAX_DATA_GAP_HOURS = READING_INTERVAL * 6;

const noOp = () => {};

export class SmoothLevelHistoryChart extends LevelColoursMixin(class {}) {
  constructor(opts) {
    super(opts);

    this.onSelect = opts.onSelect || noOp;
    this.element = opts.element;
    this.points = opts.points;
    this.unit = opts.unit;
    this.xRange = opts.xRange;
    this.yRange = opts.yRange;
    this.yTicks = opts.yTicks;

    this.highlightWidth = opts.highlightWidth || 6;
    this.highlightSelection = opts.highlightSelection;
    this.highlightManualFeedMask = opts.highlightManualFeedMask;

    this.hideAxes = opts.hideAxes;
    this.hideRefills = opts.hideRefills;
    this.hideLine = opts.hideLine;
    this.hideRawToggle = opts.hideRawToggle;

    /** {days | hours} */
    this.timeScale = opts.timeScale || 'days';

    this.svg = undefined;
    this.transitionDuration = 300;

    this.margin = {
      top: 30,
      ...this.hideAxis
        // zero all but the top margins if not displaying axes
        ? { right: 0, bottom: 0, left: 0 }
        : { right: 40, bottom: 30, left: 60 },
    };

    this.fullValue = opts.fullValue;
    this.plotInitialEstimates = opts.plotInitialEstimates || (window.localStorage.getItem('bn-feature-plotInitialEstimates') === 'true');
    this.refills = this._extractRefills();

    // don't show any forecast when we are showing a prospect refill.  The backend doesn't
    // produce a forecast on prospects so typically we would show the old forecast as if the
    // prospect is an outlier, but we have enough confidence that it's better to show the
    // refill and hide the forecast until we produce a new forecast.
    const showForecasts = _.findLast(this.points, p => !p.forecast)?.label !== 'REFILL-PROSPECT';
    if (!showForecasts) {
      this.points = this.points.filter(p => !p.forecast);
    }

    this.draw();
  }

  get closestPoint() {
    return this._closestPoint;
  }

  set closestPoint(closestPoint) {
    if (closestPoint === this._closestPoint) {
      return;
    }

    this._closestPoint = closestPoint;
    this._onClosestPointChange(closestPoint);
  }

  get closestRefill() {
    return this._closestRefill;
  }

  set closestRefill(refill) {
    if (refill === this._closestRefill) {
      return;
    }

    this._closestRefill = refill;
    this._onClosestRefillChange(refill);
  }

  get selectedPoint() {
    return this._selectedPoint;
  }

  set selectedPoint(p) {
    if (p === this._selectedPoint) {
      return;
    }

    this._selectedPoint = p;
    this._onSelectedPointChange(p);
  }

  resize() {
    const { element } = this;

    // only redraw if the element dimensions changed.  It's possible the window was resized but our host's size
    // didn't.
    if (this.lastClientHeight === element.clientHeight && this.lastClientWidth === element.clientWidth) {
      return;
    }

    d3.select(element).selectAll('*').remove();
    clearTimeout(this.resizeHandler);
    this.resizeHandler = setTimeout(() => this.draw(), 200);
  }

  draw() {
    const { element, margin } = this;
    if (!margin || !element) {
      return;
    }

    // clear any current contents
    d3.select(element).selectAll('*').remove();

    this.lastClientHeight = element.clientHeight;
    this.lastClientWidth = element.clientWidth;

    // figure out the graph dimensions
    this.height = element.clientHeight - margin.top - margin.bottom;
    this.width = element.clientWidth - margin.left - margin.right;

    if (this.height < 10 || this.width < 10) {
      return;
    }

    // add tooltip box
    d3.select(element).append('div')
      .attr('id', 'tooltip')
      .attr('class', 'tooltip');

    d3.select(element).append('div')
      .attr('id', 'tooltip2')
      .attr('class', 'tooltip');

    this.svg = d3.select(element)
      .append('svg')
      .attr('height', this.height + margin.top + margin.bottom)
      .attr('width', this.width + margin.left + margin.right)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    this.svg
      .append('rect')
      .attr('class', 'background')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    const defs = this.svg.append('defs');

    defs.append('g')
      .attr('id', 'refill-tick')
      .append('path')
      .attr('d', 'M -4,8 l 4,-8 l 4,8')
      // because <use> leverages shadow dom, it's not as easy to move this to CSS
      .attr('stroke', 'var(--gray-60)')
      .attr('stroke-width', '2px')
      .attr('fill', 'none');

    this._buildScale();

    // prevents the data rendering from going outside the axis (due to stroke widths etc)
    defs.append('clipPath')
      .attr('id', 'data-region-clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    this._drawAxes();
    this._drawData();
    this._drawOverlay();
    this._drawRefills();
    this._drawStatus();
    this._drawLabels();
  }

  _buildScale() {
    const dates = this.points.map((d) => d.date);
    const [minReading] = d3.extent(dates);

    const start = moment(this.xRange.start);
    const readingStart = minReading && moment(minReading);

    if (!readingStart || readingStart.diff(start, 'hours') > ANCHOR_TOLERANCE_HOURS) {
      dates.push(start);
    }

    dates.push(moment(this.xRange.end));
    const [min, max] = d3.extent(dates);

    this.xScale = d3.scaleTime()
      .range([0, this.width])
      .domain([
        moment(min),
        moment(max),
      ]);

    const top = Math.max(this.yRange.end, ...this.points.map((d) => d.value));
    const bottom = this.yRange.start || 0;

    // For when a bin is giving 110% effort
    this.percentAdjustment = top / this.fullValue;

    this.yScale = d3.scaleLinear()
      .domain([bottom, top])
      .range([this.height, 0]);

    this.yPercentScale = d3.scaleLinear()
      .domain([bottom / this.fullValue, top / this.fullValue])
      .range([this.height, 0]);
  }

  _drawLabels() {
    const now = moment();
    if (now.isBetween(this.xRange.start, this.xRange.end)) {
      const xNow = this.xScale(now);
      const today = this.svg.append('g').attr('class', 'label now');
      today.append('line')
        .attr('x1', xNow)
        .attr('y1', -10)
        .attr('x2', xNow)
        .attr('y2', this.height + 5);

      today.append('text')
        .attr('x', xNow)
        .attr('y', -13)
        .style('text-anchor', 'middle')
        .text('Now');
    }
  }

  _drawAxes() {
    if (this.hideAxes) {
      return;
    }

    const xAxis = d3.axisBottom(this.xScale)
      .ticks(this.timeScale === 'hours' ? d3.timeHour : d3.timeDay)
      .tickFormat(d3.timeFormat(this.timeScale === 'hours' ? '%H:00' : '%d'))
      .tickSizeOuter(0);

    const xAxis2 = d3.axisBottom(this.xScale)
      .ticks(d3.timeDay)
      .tickFormat((d) => {
        const start = moment(this.xScale.domain()[0]).add(1, 'day');
        if (this.timeScale === 'hours') {
          const day = d3.timeDay(d);
          const showDay = day < d && start < d;
          return d3.timeFormat(showDay ? '' : '%b %d')(d);
        }
        const m = d3.timeMonth(d);
        const showDay = m < d && start < d;
        return d3.timeFormat(showDay ? '' : '%b')(d);
      });

    const yAxis = d3.axisLeft(this.yScale)
      .ticks(this.yTicks || 5, 's')
      .tickSizeOuter(0);

    const percentAxis = d3.axisRight(this.yPercentScale)
      .tickFormat(d3.format('~%'))
      .tickSizeInner(-this.width + 5);

    const isZoomed = this.yRange.start > 0 || this.yRange.end < this.fullTotal;
    if (isZoomed) {
      percentAxis.ticks(this.yTicks || 5);
    } else {
      percentAxis.tickValues([0, 0.25, 0.5, 0.75, 1]);
    }

    this.svg.append('g')
      .attr('class', 'y axis')
      .transition()
      .duration(this.transitionDuration)
      .call(yAxis);

    this.svg.append('g')
      .attr('class', 'y axis percent')
      .attr('transform', `translate(${this.width},0)`)
      .transition()
      .duration(this.transitionDuration)
      .call(percentAxis);

    this.svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left * .7)
      .attr('x', 0 - this.height / 2)
      .style('text-anchor', 'middle')
      .text(this.unit);

    this.svg.append('g')
      .attr('class', 'x axis')
      .attr('transform', () => `translate(0,${this.height})`)
      .transition()
      .duration(this.transitionDuration)
      .call(xAxis);

    this.svg.append('g')
      .attr('class', 'x axis no-ticks')
      .attr('transform', () => `translate(0,${this.height + 10})`)
      .transition()
      .duration(this.transitionDuration)
      .call(xAxis2);
  }

  // status messages, like "no data" or "loading"
  _drawStatus() {
    this.svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    this.svg.append('text')
      .attr('class', 'no-data-message')
      .attr('x', this.width / 2)
      .attr('y', this.height / 2)
      .style('text-anchor', 'middle')
      .text('No data available');

    this.svg.append('text')
      .attr('class', 'loading-message')
      .attr('x', this.width / 2)
      .attr('y', this.height / 2)
      .style('text-anchor', 'middle')
      .text('Loading data...');
  }

  _extractRefills() {
    const refills = [];

    for (let i = 0; i < this.points.length; i++) {
      const curr = this.points[i];
      if (!this._isRefill(curr)) {
        continue;
      }
      const refill = { ...curr };
      const prev = this.points[i - 1];
      if (prev) {
        refill.height = this._bestValue(curr) - this._bestValue(prev);
      }

      refill.start = prev?.label === 'PARTIAL_REFILL' ? prev.date : refill.date;
      refills.push(refill);
    }

    return refills;
  }

  _drawRefills() {
    if (this.hideRefills) {
      return;
    }
    this.refills.forEach(refill => {
      const { date } = refill;
      const x = this.xScale(date);
      const label = this.svg.append('g').attr('class', 'label');

      const margin = 6;
      const arrowHeight = 8;
      const arrowSpacing = 6;
      const start = this.yScale(this._bestValue(refill));
      const end = this.yScale(this._bestValue(refill) - refill.height);
      const height = end - start;
      const arrowCount = Math.max(1, Math.floor((height - margin * 2) / (arrowHeight + arrowSpacing)));
      const adjustedHeight = arrowCount * arrowHeight + (arrowCount - 1) * arrowSpacing;
      const adjustedMargin = (height - adjustedHeight) / 2;
      const arrowYs = [];
      for (let i = 0; i < arrowCount; i++) {
        arrowYs.push(start + adjustedMargin + i * (arrowHeight + arrowSpacing));
      }

      label
        .selectAll('refill-marker')
        .data(arrowYs)
        .enter()
        .append('use')
        .attr('xlink:href', '#refill-tick')
        .attr('x', x)
        .attr('y', d => d);
    });
  }

  _drawData() {
    const thresholds = [...this.thresholds].sort((a, b) => a.percent - b.percent);
    const gradientStops = [{ ...thresholds[0], percent: 0 }, thresholds[0]];

    for (let i = 1; i < thresholds.length; i++) {
      gradientStops.push({ ...thresholds[i], percent: thresholds[i - 1].percent + 0.01 });
      gradientStops.push(thresholds[i]);
    }

    this.svg.append('linearGradient')
      .attr('id', 'temperature-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0).attr('y1', this.height)
      .attr('x2', 0).attr('y2', 0)
      .selectAll('stop')
      .data(gradientStops)
      .enter().append('stop')
      .attr('offset', (d) => `${d.percent / this.percentAdjustment}%`)
      .attr('stop-color', (d) => d.darkColour);

    const makeLine = property => d3.line()
      .curve(d3.curveLinear)
      .x((d) => this.xScale(d.date))
      .y((d) => this.yScale(typeof d[property] !== 'undefined' ? d[property] : d.value));

    const [forecasts, readings] = _.partition(this.points, p => p.forecast);
    const readingsWithAnchors = [...readings.filter(({ label }) => label !== 'OUTLIER')];

    // insert fake points at refill points to make the refill look instantaneous when graphing.  These points are not
    // shown and will not be snapped to with the crosshairs
    let prevReadingWasRefill = false;
    for (let i = 1; i < readingsWithAnchors.length; i++) {
      let prevReading = readingsWithAnchors[i - 1];
      let reading = readingsWithAnchors[i];

      if (this._exceedsMaxGap(prevReading, reading)) {
        readingsWithAnchors.splice(i, 0, {
          date: prevReading.date,
          value: 0,
          estimatedValue: 0,
          initialEstimatedValue: 0,
          disjoint: true,
        },{
          date: reading.date,
          value: 0,
          estimatedValue: 0,
          initialEstimatedValue: 0,
          disjoint: true,
        });
        i += 2;
        // prev reading should now be the right gap anchor, in case it's also a refill
        prevReading = readingsWithAnchors[i - 1];
      }

      if (this._isRefill(reading)) {
        // in the case of two successive refills, we don't want to colour the line at the partial refill
        if (prevReadingWasRefill) {
          readingsWithAnchors[i - 1] = { ...readingsWithAnchors[i - 1], disjoint: true };
        }

        // these are only aids in rendering the line; they are not real data points
        readingsWithAnchors.splice(i, 0, {
          date: reading.date,
          value: prevReading.value,
          estimatedValue: prevReading.estimatedValue,
          initialEstimatedValue: prevReading.initialEstimatedValue,
        },{
          date: reading.date,
          value: reading.value,
          estimatedValue: reading.estimatedValue,
          initialEstimatedValue: reading.initialEstimatedValue,
          disjoint: true,
        });
        prevReadingWasRefill = true;
        i += 2;
      } else {
        prevReadingWasRefill = false;
      }
    }

    const lastReading = readings[readings.length - 1];
    if (lastReading && forecasts.length > 0) {
      forecasts.unshift({
        ...lastReading,
        value: this._bestValue(lastReading),
      });
    }

    // disjoint prevents us from connecting the line before or after the disjoint point.
    const pastLine = makeLine('estimatedValue').defined(d => !d.disjoint);

    const pastArea = d3.area()
      .x(pastLine.x())
      .y1(pastLine.y())
      .y0(this.yScale(0));

    const forecastLine = makeLine('value');

    const forecastArea = d3.area()
      .x(forecastLine.x())
      .y1(forecastLine.y())
      .y0(this.yScale(0));

    this.svg.append('path')
      .datum(forecasts)
      .attr('d', forecastArea)
      .attr('class', 'fill-area forecast')
      .attr('fill-opacity', '0')
      .attr('clip-path', 'url(#data-region-clip)')
      .transition()
      .ease(d3.easeExpOut)
      .duration(this.transitionDuration)
      .attr('fill-opacity', 0.3);

    this.svg.append('path')
      .datum(forecasts)
      .attr('d', forecastLine)
      .attr('class', 'fill-line forecast')
      .attr('stroke-opacity', 0)
      .attr('clip-path', 'url(#data-region-clip)')
      .transition()
      .ease(d3.easeExpOut)
      .duration(this.transitionDuration)
      .attr('stroke-opacity', 1);

    if (!this.hideLine) {
      this.svg.append('path')
        .datum(readingsWithAnchors)
        .attr('d', pastLine)
        .attr('class', 'fill-line past-estimates')
        .attr('stroke', 'url(#temperature-gradient)')
        .attr('stroke-opacity', 0)
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('stroke-opacity', 1);
    }

    if (this.plotInitialEstimates) {
      this.svg.append('path')
        .datum(readings)
        .attr('d', makeLine('initialEstimatedValue'))
        .attr('class', 'fill-line initial-estimates')
        .attr('stroke-opacity', 0)
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('stroke-opacity', 0.3);

      this.svg.append('path')
        .datum(readingsWithAnchors)
        .attr('d', pastArea)
        .attr('class', 'fill-area past-estimates')
        .attr('fill-opacity', '0')
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('fill-opacity', 0.3);
    }

    this.svg.selectAll('rawPoints')
      .data(readings)
      .enter()
      .append('circle')
      .attr('class', 'raw-point')
      .classed('outlier', ({ label }) => label === 'OUTLIER')
      .classed('prospect', ({ label }) => label === 'REFILL-PROSPECT')
      .classed('manual', ({ source }) => source === 'manual')
      .attr('fill', d => this._getThresholdColour(d, this.hideLine ? 'colour' : 'darkerColour'))
      .attr('cx', d => this.xScale(d.date))
      .attr('cy', (d) => this.yScale(d.value))
      .attr('r', ({ source }) => source === 'manual' ? 3 : 2);

    // show manual feed mask marks
    if (this.highlightManualFeedMask) {
      // draw a line at the bottom of every point with a feedMask
      this.svg.selectAll('feedMaskMarks')
        .data(readings.filter(r => r.feedMask))
        .enter()
        .append('rect')
        .attr('x', d => this.xScale(d.date) - (this.highlightWidth / 2))
        .attr('y', this.height - 5)
        .attr('height', 3)
        .attr('width', this.highlightWidth)
        .attr('class', 'manual-feed-mask');
    }
  }

  _drawOverlay() {
    // show what's currently selected
    this.selection = this.svg
      .append('g')
      .style('display', 'none')
      .attr('class', 'selection');
    this.selection
      .append('circle')
      .attr('r', 5)
      .attr('id', 'selectionCircle')
      .attr('class', 'selectionCircle');
    this.selection
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.highlightWidth)
      .attr('id', 'selectionRect')
      .attr('class', 'selectionRect');

    // show where mouse is hovering
    this.focus = this.svg
      .append('g')
      .style('display', 'none')
      .attr('class', 'focus');
    this.focus
      .append('line')
      .attr('id', 'focusLineX')
      .attr('class', 'focusLine');
    this.focus
      .append('line')
      .attr('id', 'focusLineY')
      .attr('class', 'focusLine');
    this.focus
      .append('circle')
      .attr('r', 5)
      .attr('id', 'focusCircle')
      .attr('class', 'focusCircle');

    this.svg
      .on('click', () => this._selectPoint(this.closestPoint))
      .on('mouseover', () => this.focus.style('display', null))
      .on('mousemove', event => {
        const d3Mouse = d3.pointer(event);
        const date = this.xScale.invert(d3Mouse[0]);
        this.closestPoint = this._findClosestPoint(date);
      })
      .on('mouseout', () => {
        this.focus.style('display', 'none');
        this.closestPoint = null;
      });
  }

  _findClosestPoint(date) {
    date = moment(date);
    const bisect = d3.bisector((d) => d.date).left;
    const i = bisect(this.points, date);
    // get closest point
    const p1 = this.points[i - 1];
    const p2 = this.points[i];
    if (p1 && p2) {
      return date.diff(p1.date) > p2.date.diff(date) ? p2 : p1;
    }
    return p1 || p2;
  }

  _onClosestPointChange(closestPoint) {
    if (!this.closestPoint) {
      this._tip();
      this.closestRefill = null;
      return;
    }

    const x = this.xScale(closestPoint.date);
    const y = this.yScale(this._bestValue(closestPoint));
    // draw circle
    this.focus.select('#focusCircle')
      .attr('cx', x)
      .attr('cy', y);
    // draw lines
    this.focus.select('#focusLineX')
      .attr('x1', x).attr('y1', 0)
      .attr('x2', x).attr('y2', this.height);
    this.focus.select('#focusLineY')
      .attr('x1', 0).attr('y1', y)
      .attr('x2', this.width).attr('y2', y);
    this._tip(this._tipFormat(closestPoint));

    this.closestRefill = this.refills.find(({ label, start }, i) => {
      const next = this.refills[i + 1];
      const { date } = closestPoint;
      return start <= date && label === 'REFILL' && (!next?.date || (date < next.date));
    });
  }

  _onClosestRefillChange(refill) {
    if (refill?.date && !this.hideRefills) {
      const date = moment(refill.date).format('MMM D, h a');
      this._tip2(`Refilled on ${date}`);
    } else {
      this._tip2();
    }
  }

  _onSelectedPointChange(p) {
    this.selection.style('display', (p && this.highlightSelection) ? null : 'none');

    if (!p) {
      return;
    }
    const x = this.xScale(p.date);
    const y = this.yScale(this._bestValue(p));
    this.selection.select('#selectionCircle')
      .attr('cx', x)
      .attr('cy', y);

    this.selection.select('#selectionRect')
      .attr('x', x - (this.highlightWidth / 2));
  }

  _selectPoint(p) {
    this.selectedPoint = p;
    this.onSelect(p);
  }

  selectByDate(date) {
    this._selectPoint(this._findClosestPoint(date));
  }

  selectPrevious() {
    if (!this.selectedPoint) {
      this._selectPoint(this.points[this.points.length - 1]);
    }
    const i = this.points.indexOf(this.selectedPoint);
    if (i < 1) {
      return;
    }
    this._selectPoint(this.points[i - 1]);
  }

  selectNext() {
    if (!this.selectedPoint) {
      this._selectPoint(this.points[0]);
    }
    const i = this.points.indexOf(this.selectedPoint);
    if (i === -1 || i === this.points.length - 1) {
      return;
    }
    this._selectPoint(this.points[i + 1]);
  }

  _getThresholdColour(d, variant = 'colour') {
    if (d.source === 'manual') {
      return 'var(--gray-60)';
    }
    return this.getLevelColour(100 * d.value / this.fullValue, variant);
  }

  _updateTip(id, html) {
    const el = d3.select(this.element).select(id);
    if (!html) {
      el.html('').style('display', 'none');
    } else {
      el.html(html).style('display', null);
    }
  }

  _tip(text) {
    this._updateTip('#tooltip', text);
  }

  _tip2(text) {
    this._updateTip('#tooltip2', text);
  }

  _tipFormat(d) {
    const date = this.timeScale === 'hours'
      ? moment(d.date).format('MMM D, H:mm')
      : moment(d.date).format('MMM D, h:mm a');
    let context;
    if (d.forecast) {
      context = 'Forecast';
    } else if (d.source === 'manual') {
      context = 'Manual Inventory';
    }
    return `${`${date} - <span class="magnitude">${this._bestValue(d)}</span> ${this.unit}` +
      `${context ? ` [${context}]` : ''}`}${
      !d.forecast ? `<span class="raw">Raw reading: ${d.value} ${this.unit}</span>` : ''}`;
  }

  _bestValue(point) {
    if (!point) {
      return;
    }
    if (typeof point.estimatedValue !== 'undefined') {
      return point.estimatedValue;
    }

    return point.value;
  }

  _exceedsMaxGap(first, second) {
    if (!first || !second) {
      return false;
    }

    return moment.duration(second.date.diff(first.date)).asHours() > MAX_DATA_GAP_HOURS;
  }

  _isRefill(point) {
    return ['REFILL', 'PARTIAL_REFILL', 'REFILL-PROSPECT'].includes(point?.label);
  }
}
